import { useRef } from "react";
import type { PropsWithChildren } from "react";
import { atom, useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { useDtfConfig, useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";

type DtfSideTracking = {
  markSideAsActive: (side: string, processTimeStamp: string) => void;
  markSideAsDeleted: (side: string) => void;
  isSideActive: (side: string, processTimeStamp: string) => boolean;
};

const dtfSideTrackingAtom = atom<DtfSideTracking | null>(null);

function useDtfInitCreator() {
  const activeSides = useRef<Record<string, string>>({});
  const initConfig = useDtfConfig();
  const channel = useDtfChannel();

  const markSideAsActive = (side: string, processTimeStamp: string) => {
    activeSides.current[side] = processTimeStamp;
  };

  const markSideAsDeleted = (side: string) => {
    delete activeSides.current[side];
  };

  const isSideActive = (side: string, processTimeStamp: string) =>
    activeSides.current[side] === processTimeStamp;

  return {
    initConfig,
    channel,
    markSideAsActive,
    markSideAsDeleted,
    isSideActive
  };
}

// Re-export the hooks for backward compatibility
export { useDtfConfig, useDtfChannel };

export function DtfInitProvider({ children }: PropsWithChildren) {
  const value = useDtfInitCreator();
  useHydrateAtoms(
    [
      [
        dtfSideTrackingAtom,
        {
          markSideAsActive: value.markSideAsActive,
          markSideAsDeleted: value.markSideAsDeleted,
          isSideActive: value.isSideActive
        }
      ]
    ],
    { dangerouslyForceHydrate: true }
  );

  return children;
}

export function useDtfSideTracking() {
  const tracking = useAtomValue(dtfSideTrackingAtom);
  if (!tracking) {
    throw new Error("DtfInitProvider is missing");
  }
  return tracking;
}
